import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  IconButton,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Flex,
  Heading,
  Badge,
} from "@chakra-ui/react";
import { ChatIcon, CloseIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import axios from "axios";
import proxy from "../../proxy";

const POLL_MS = 4000;
const TOKEN_KEY = "chat_visitor_token";
const NAME_KEY = "chat_visitor_name";

// token persistent per vizitator
const getToken = () => {
  let t = localStorage.getItem(TOKEN_KEY);
  if (!t) {
    t =
      "v_" +
      Date.now().toString(36) +
      Math.random().toString(36).slice(2, 10);
    localStorage.setItem(TOKEN_KEY, t);
  }
  return t;
};

const formatTime = (s) =>
  new Date((s || "").replace(" ", "T")).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [nameInput, setNameInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);

  const tokenRef = useRef(getToken());
  const lastIdRef = useRef(0);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current)
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${proxy}server_mts/chat/Poll.php`, {
        params: { token: tokenRef.current, after: lastIdRef.current },
        headers: { "Cache-Control": "no-cache" },
      });
      const incoming = res.data?.messages || [];
      if (incoming.length) {
        lastIdRef.current = incoming[incoming.length - 1].id;
        setMessages((prev) => [...prev, ...incoming]);
        // mesaje noi de la admin cand fereastra e inchisa
        const fromAdmin = incoming.filter((m) => m.sender === "admin").length;
        if (fromAdmin) setUnread((u) => (isOpen ? 0 : u + fromAdmin));
        scrollToBottom();
      }
    } catch (e) {
      /* silentios — reincearca la urmatorul tick */
    }
  }, [isOpen]);

  // polling permanent (si cand e inchis, ca sa stim de mesaje noi)
  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    if (isOpen) {
      setUnread(0);
      scrollToBottom();
    }
  }, [isOpen]);

  const saveName = () => {
    const n = nameInput.trim();
    if (!n) return;
    localStorage.setItem(NAME_KEY, n);
    setName(n);
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    // optimist
    const optimistic = {
      id: `tmp_${Date.now()}`,
      sender: "visitor",
      body,
      created_at: new Date().toISOString().slice(0, 19).replace("T", " "),
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    scrollToBottom();
    try {
      await axios.post(`${proxy}server_mts/chat/Send.php`, {
        token: tokenRef.current,
        name,
        body,
      });
      // poll va aduce versiunea reala cu id corect
      poll();
    } catch (e) {
      // marcheaza eroare
      setMessages((prev) =>
        prev.map((m) =>
          m.id === optimistic.id ? { ...m, _failed: true } : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Box position="fixed" bottom="20px" right="20px" zIndex={2000}>
      {/* Fereastra de chat */}
      {isOpen && (
        <Flex
          direction="column"
          w={{ base: "calc(100vw - 32px)", sm: "360px" }}
          maxW="360px"
          h="500px"
          maxH="70vh"
          bg="gray.900"
          border="1px solid"
          borderColor="whiteAlpha.200"
          borderRadius="xl"
          boxShadow="0 12px 40px rgba(0,0,0,0.5)"
          overflow="hidden"
          mb={3}
        >
          {/* Header */}
          <HStack
            justify="space-between"
            px={4}
            py={3}
            bg="linear-gradient(135deg, #2C7A7B, #285E61)"
          >
            <VStack align="start" spacing={0}>
              <Heading size="sm" color="white">
                💬 Chat
              </Heading>
              <Text fontSize="2xs" color="whiteAlpha.800">
                Mattias Assets
              </Text>
            </VStack>
            <IconButton
              icon={<CloseIcon boxSize={3} />}
              size="sm"
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.300" }}
              aria-label="Close"
              onClick={() => setIsOpen(false)}
            />
          </HStack>

          {/* Continut */}
          {!name ? (
            // pas de introducere nume
            <VStack flex="1" justify="center" px={6} spacing={4}>
              <Text color="gray.300" fontSize="sm" textAlign="center">
                Hi! 👋 What's your name?
              </Text>
              <Input
                placeholder="Your name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveName()}
                bg="gray.800"
                color="white"
                borderColor="whiteAlpha.300"
                size="sm"
              />
              <Button
                colorScheme="teal"
                size="sm"
                w="full"
                onClick={saveName}
                isDisabled={!nameInput.trim()}
              >
                Start chatting
              </Button>
            </VStack>
          ) : (
            <>
              {/* Lista mesaje */}
              <VStack
                ref={scrollRef}
                flex="1"
                align="stretch"
                spacing={2}
                px={3}
                py={3}
                overflowY="auto"
              >
                {messages.length === 0 && (
                  <Text color="gray.500" fontSize="xs" textAlign="center" mt={4}>
                    Send us a message — we'll reply as soon as we can.
                  </Text>
                )}
                {messages.map((m) => {
                  const mine = m.sender === "visitor";
                  return (
                    <Flex
                      key={m.id}
                      justify={mine ? "flex-end" : "flex-start"}
                    >
                      <Box
                        maxW="78%"
                        px={3}
                        py={2}
                        borderRadius="lg"
                        bg={mine ? "teal.500" : "gray.700"}
                        color="white"
                        opacity={m._optimistic ? 0.7 : 1}
                        borderWidth={m._failed ? "1px" : 0}
                        borderColor="red.400"
                      >
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {m.body}
                        </Text>
                        <Text
                          fontSize="2xs"
                          color="whiteAlpha.700"
                          textAlign="right"
                          mt={0.5}
                        >
                          {m._failed ? "not sent" : formatTime(m.created_at)}
                        </Text>
                      </Box>
                    </Flex>
                  );
                })}
              </VStack>

              {/* Input */}
              <HStack p={2} borderTop="1px solid" borderColor="whiteAlpha.200">
                <Input
                  placeholder="Type a message…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !e.shiftKey
                      ? (e.preventDefault(), send())
                      : null
                  }
                  bg="gray.800"
                  color="white"
                  borderColor="whiteAlpha.300"
                  size="sm"
                />
                <IconButton
                  icon={<ArrowForwardIcon />}
                  colorScheme="teal"
                  size="sm"
                  aria-label="Send"
                  onClick={send}
                  isLoading={sending}
                  isDisabled={!draft.trim()}
                />
              </HStack>
            </>
          )}
        </Flex>
      )}

      {/* Buton flotant */}
      <Flex justify="flex-end">
        <Box position="relative">
          <IconButton
            icon={isOpen ? <CloseIcon /> : <ChatIcon />}
            colorScheme="teal"
            size="lg"
            isRound
            boxShadow="0 6px 20px rgba(0,0,0,0.4)"
            aria-label="Chat"
            onClick={() => setIsOpen((o) => !o)}
          />
          {!isOpen && unread > 0 && (
            <Badge
              position="absolute"
              top="-4px"
              right="-4px"
              bg="red.500"
              color="white"
              borderRadius="full"
              fontSize="2xs"
              minW="18px"
              textAlign="center"
            >
              {unread}
            </Badge>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default ChatWidget;
